/**
 * Optimization Findings Side Panel Component
 * Displays detailed information about a selected optimization finding
 */

import React, { useState } from 'react';
import { Button, Chip, Icon, SidePanel } from '@saviynt/design-system';

import { SHARED_HUMAN_IDENTITIES } from '../../../../../mock-data/sharedLicenseDummyData';

import type {
  AccountNode,
  AccountRootNode,
  ReviewListItem,
  TreeRootNode,
  UserTreeNode,
} from './ReviewListModal/ReviewListModal';
import OPTIMIZATION_FINDINGS_PANEL_CONSTANTS from './constants';
import { ReviewListModal } from './ReviewListModal';
import type { OptimizationFindingsPanelProps } from './type';

import './OptimizationFindingsPanel.css';

/** Mock analysis data — not derived from the API */
const MOCK_SAVI_ANALYSIS = [
  'Usage pattern analysis indicates zero transactions in the last 90 days',
  'No scheduled jobs or batch processes detected for these accounts',
  'License tier consumption confirms full FUE allocation without utilization',
  'Recommendation validated against organizational role mapping',
];

/**
 * Displays a side panel with detailed information about a selected
 * optimization finding, including metrics, savings, Savi analysis, and actions
 * to review or apply the recommendation.
 * @param {OptimizationFindingsPanelProps} props - Component props
 * @param {boolean} props.isOpen - Whether the side panel is visible
 * @param {() => void} props.onClose - Callback invoked when the panel is closed
 * @param {OptimizationFinding | null} props.finding - The finding to display, or null to render nothing
 * @param {(finding: OptimizationFinding) => void} [props.onApply] - Optional callback invoked when the user applies the recommendation
 * @param {boolean} [props.showDeprovision] - Whether to show the Deprovision All button
 * @returns {React.ReactElement | null} The rendered side panel, or null when no finding is provided
 */
function OptimizationFindingsPanel({
  isOpen,
  onClose,
  finding,
  onApply,
  showDeprovision = false,
}: OptimizationFindingsPanelProps): React.ReactElement | null {
  const [isReviewListOpen, setIsReviewListOpen] = useState(false);
  const [reviewListItems, setReviewListItems] = useState<ReviewListItem[]>([]);

  const mockTreeData: UserTreeNode = {
    id: 'USR00002 ( Sarah Johnson )',
    assignedTier: 'Advanced',
    recommendedTier: 'Core',
    accounts: [
      {
        id: 'Sarah_AC_Johnson',
        assignedTier: 'Advanced',
        roles: [
          {
            id: 'SD_SALES_REP',
            authObjects: [
              {
                id: 'A_CFG_CMP',
                tier: 'Advanced',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '05' },
                  { field: 'ACTVT', value: '06' },
                ],
              },
              {
                id: 'A_UO',
                tier: 'Core',
                used: true,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '06' },
                ],
              },
              {
                id: 'F_BKPF_BUK',
                tier: 'Core',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '22' },
                  { field: 'ACTVT', value: '43' },
                  { field: 'ACTVT', value: 'C4' },
                ],
              },
            ],
          },
          {
            id: 'FI_AP_CLERK',
            authObjects: [
              {
                id: 'BEH_SUBSCR',
                tier: 'Advanced',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '06' },
                ],
              },
              {
                id: 'C_DT_CNV',
                tier: 'Core',
                used: true,
                fields: [{ field: 'ACTVT', value: '02' }],
              },
              {
                id: 'C_DT_FND',
                tier: 'Core',
                used: false,
                fields: [{ field: 'ACTVT', value: '02' }],
              },
            ],
          },
          {
            id: 'COST_ANALYST',
            authObjects: [
              {
                id: 'CIM_ADMIN',
                tier: 'Advanced',
                used: true,
                fields: [{ field: 'ACTVT', value: '01' }],
              },
              {
                id: 'CM_CON',
                tier: 'Core',
                used: false,
                fields: [{ field: 'ACTVT', value: '60' }],
              },
            ],
          },
        ],
      } as AccountNode,
    ],
  };

  const mockTreeData2: UserTreeNode = {
    id: 'USR00003 ( Rajesh Patel )',
    assignedTier: 'Advanced',
    recommendedTier: 'Core',
    accounts: [
      {
        id: 'Rajesh_AC_Patel',
        assignedTier: 'Advanced',
        roles: [
          {
            id: 'MM_PURCHASER',
            authObjects: [
              {
                id: 'C_PSCVP_DB',
                tier: 'Advanced',
                used: false,
                fields: [{ field: 'ACTVT', value: '02' }],
              },
              {
                id: 'C_SIT2INST',
                tier: 'Core',
                used: true,
                fields: [{ field: 'ACTVT', value: '06' }],
              },
              {
                id: 'C_SIT2TYPE',
                tier: 'Core',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '06' },
                ],
              },
            ],
          },
          {
            id: 'CONTROLLER',
            authObjects: [
              {
                id: 'CRM_SE_ENT',
                tier: 'Advanced',
                used: true,
                fields: [{ field: 'ACTVT', value: '02' }],
              },
              {
                id: 'DMEX_TREE',
                tier: 'Core',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '34' },
                  { field: 'ACTVT', value: '71' },
                ],
              },
            ],
          },
        ],
      } as AccountNode,
    ],
  };

  const mockTreeData3: UserTreeNode = {
    id: 'USR00008 ( Michael Lee )',
    assignedTier: 'Advanced',
    recommendedTier: 'Core',
    accounts: [
      {
        id: 'Michael_AC_Lee',
        assignedTier: 'Advanced',
        roles: [
          {
            id: 'FI_AR_CLERK',
            authObjects: [
              {
                id: 'E_CS_BUNIT',
                tier: 'Advanced',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '06' },
                ],
              },
              {
                id: 'E_CS_CACTT',
                tier: 'Core',
                used: true,
                fields: [
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '05' },
                  { field: 'ACTVT', value: '16' },
                  { field: 'ACTVT', value: '95' },
                ],
              },
            ],
          },
          {
            id: 'GL_ACCOUNTANT',
            authObjects: [
              {
                id: 'E_CS_CONGR',
                tier: 'Core',
                used: true,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '06' },
                ],
              },
              {
                id: 'E_CS_DIMEN',
                tier: 'Core',
                used: false,
                fields: [
                  { field: 'ACTVT', value: '01' },
                  { field: 'ACTVT', value: '02' },
                  { field: 'ACTVT', value: '06' },
                  { field: 'ACTVT', value: '16' },
                ],
              },
            ],
          },
        ],
      } as AccountNode,
    ],
  };

  const mockAccountRoot: AccountRootNode = {
    kind: 'account-root',
    id: 'Emily_AC_Davis',
    assignedTier: 'Advanced',
    recommendedTier: 'Core',
    roles: [
      {
        id: 'FINANCE_MANAGER',
        authObjects: [
          {
            id: 'E_CS_ITCLG',
            tier: 'Advanced',
            used: false,
            fields: [
              { field: 'ACTVT', value: '01' },
              { field: 'ACTVT', value: '02' },
            ],
          },
          {
            id: 'E_CS_RVERS',
            tier: 'Core',
            used: true,
            fields: [
              { field: 'ACTVT', value: '02' },
              { field: 'ACTVT', value: '16' },
            ],
          },
          {
            id: 'EI_VIEW_BL',
            tier: 'Core',
            used: false,
            fields: [
              { field: 'ACTVT', value: '01' },
              { field: 'ACTVT', value: '06' },
            ],
          },
        ],
      },
      {
        id: 'FI_VIEWER',
        authObjects: [
          {
            id: 'EI_VIEW_CR',
            tier: 'Core',
            used: true,
            fields: [
              { field: 'ACTVT', value: '01' },
              { field: 'ACTVT', value: '02' },
              { field: 'ACTVT', value: '06' },
            ],
          },
          {
            id: 'F_ACES_DST',
            tier: 'Advanced',
            used: null,
            fields: [
              { field: 'ACTVT', value: '01' },
              { field: 'ACTVT', value: '02' },
              { field: 'ACTVT', value: '06' },
            ],
          },
        ],
      },
    ],
  };

  const allTreeData: TreeRootNode[] = [
    mockTreeData,
    mockTreeData2,
    mockTreeData3,
    mockAccountRoot,
  ];

  if (!finding) {
    return null;
  }

  // Review list data — derived from the shared dummy-data module so the
  // identities shown here are consistent with the FUE Inventory Human rows.
  const generateMockReviewItems = (): ReviewListItem[] => {
    const statuses: ('Over-licensed' | 'Dormant' | 'Active' | 'Compliant')[] = [
      'Compliant',
      'Over-licensed',
      'Compliant',
      'Compliant',
      'Over-licensed',
      'Compliant',
    ];

    return SHARED_HUMAN_IDENTITIES.map((identity, i) => ({
      id: identity.identityId,
      // Match the FUE Inventory Human "userId" column: "username (Full Name)".
      userId: `${identity.userId} (${identity.fullName})`,
      fullName: identity.fullName,
      department: identity.department,
      identityType: identity.identityType,
      current: 'Advanced (1)',
      status: statuses[i % statuses.length],
    }));
  };

  const getMetricChipColor = (
    value: string
  ): 'success' | 'warning' | 'critical' => {
    switch (value) {
      case 'High':
        return 'critical';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'success';
      default:
        return 'warning';
    }
  };

  const getCategoryChipColor = (
    category: string
  ): 'success' | 'info' | 'warning' | 'critical' => {
    switch (category) {
      case 'Cost Reduction':
        return 'success';
      case 'License Change':
        return 'info';
      case 'Role Optimization':
        return 'warning';
      default:
        return 'info';
    }
  };

  const handleApply = (): void => {
    onApply?.(finding);
    onClose();
  };

  const handleReviewList = (): void => {
    setReviewListItems(generateMockReviewItems());
    setIsReviewListOpen(true);
  };

  const handleReviewListClose = (): void => {
    setIsReviewListOpen(false);
  };

  return (
    <>
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        header={{
          title: finding.title,
          headerIcon: (
            <Icon
              kind='SaviCompanion'
              size='small'
              className='LicenseManagement-OptimizationFindingsPanel-saviIcon'
            />
          ),
        }}
        layout={
          <div className='LicenseManagement-OptimizationFindingsPanel'>
            {/* Body */}
            <div className='LicenseManagement-OptimizationFindingsPanel-body'>
              {/* Finding Description with Category Chip */}
              <div className='LicenseManagement-OptimizationFindingsPanel-section'>
                <div className='LicenseManagement-OptimizationFindingsPanel-descriptionRow'>
                  <p className='LicenseManagement-OptimizationFindingsPanel-descriptionText'>
                    {finding.reasonText}
                  </p>
                  <Chip
                    kind='subtleFilled'
                    label={finding.category}
                    color={getCategoryChipColor(finding.category)}
                    size='small'
                  />
                </div>
              </div>

              {/* Metrics Section - Inline Layout */}
              <div className='LicenseManagement-OptimizationFindingsPanel-metricsInline'>
                {/* Impact */}
                <div className='LicenseManagement-OptimizationFindingsPanel-metricInlineItem'>
                  <span className='LicenseManagement-OptimizationFindingsPanel-metricInlineLabel'>
                    {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.IMPACT}
                  </span>
                  <Chip
                    kind='outlined'
                    label={finding.impact}
                    color={getMetricChipColor(finding.impact)}
                    size='small'
                  />
                </div>

                {/* Effort */}
                <div className='LicenseManagement-OptimizationFindingsPanel-metricInlineItem'>
                  <span className='LicenseManagement-OptimizationFindingsPanel-metricInlineLabel'>
                    {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.EFFORT}
                  </span>
                  <Chip
                    kind='outlined'
                    label={finding.effort}
                    color={getMetricChipColor(finding.effort)}
                    size='small'
                  />
                </div>
              </div>

              {/* Savings Section */}
              <div className='LicenseManagement-OptimizationFindingsPanel-savingsSection'>
                <p className='LicenseManagement-OptimizationFindingsPanel-savingsAmount'>
                  {finding.savingsDisplayValue}
                </p>
                <p className='LicenseManagement-OptimizationFindingsPanel-savingsLabel'>
                  {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.LICENSE_SAVINGS}
                </p>
              </div>

              {/* Savi Analysis Section - uses mock data */}
              <div className='LicenseManagement-OptimizationFindingsPanel-analysisSection'>
                <div className='LicenseManagement-OptimizationFindingsPanel-analysisLabel'>
                  {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.SAVI_ANALYSIS}
                </div>
                <div className='LicenseManagement-OptimizationFindingsPanel-analysisBox'>
                  {MOCK_SAVI_ANALYSIS.map((item) => (
                    <div
                      key={item}
                      className='LicenseManagement-OptimizationFindingsPanel-analysisItem'>
                      <p className='LicenseManagement-OptimizationFindingsPanel-analysisText'>
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }
        footer={
          <div className='LicenseManagement-OptimizationFindingsPanel-footer'>
            <Button
              kind='ghost'
              onClick={onClose}
              className='LicenseManagement-OptimizationFindingsPanel-closeBtn'>
              {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.CLOSE}
            </Button>
            <div className='LicenseManagement-OptimizationFindingsPanel-footerActions'>
              <Button
                kind='outlined'
                onClick={handleReviewList}
                className='LicenseManagement-OptimizationFindingsPanel-reviewBtn'>
                {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.REVIEW_LIST}
              </Button>
              {showDeprovision && (
                <Button
                  kind='filled'
                  onClick={handleApply}
                  className='LicenseManagement-OptimizationFindingsPanel-applyBtn'>
                  {OPTIMIZATION_FINDINGS_PANEL_CONSTANTS.DEPROVISION_ALL}
                </Button>
              )}
            </div>
          </div>
        }
        className='LicenseManagement-OptimizationFindingsPanel-sidePanel'
      />

      {/* Review List Modal */}
      <ReviewListModal
        isOpen={isReviewListOpen}
        onClose={handleReviewListClose}
        finding={finding}
        items={reviewListItems}
        treeData={allTreeData}
      />
    </>
  );
}

export default OptimizationFindingsPanel;
